/**
 * Small UA/capability probes. Kept together so the user-agent sniffing is confined to
 * one file rather than sprinkled through components.
 */

/**
 * Is this a desktop (no dialer)?
 *
 * A `tel:` link hands off to the dialer on a phone, which is exactly right; on a desktop
 * it hands off to nothing, so there we reveal the number in a sheet with a copy button.
 */
export function isDesktop() {
  const ua = navigator.userAgent || "";
  if (/Android|iPhone|iPad|iPod|Windows Phone|Mobile/i.test(ua)) return false;
  if (navigator.maxTouchPoints > 1) return false; // iPadOS reports itself as a Mac
  return !(window.matchMedia && window.matchMedia("(pointer:coarse)").matches);
}

/**
 * Which browser's permission UI should the "access is blocked" instructions describe?
 * Returning one of a small set keeps the sheet showing a single, correct set of steps
 * instead of a wall of alternatives.
 *
 * @returns {"chrome"|"safari"|"samsung"}
 */
export function detectBrowser() {
  const ua = navigator.userAgent || "";
  if (/SamsungBrowser/i.test(ua)) return "samsung";
  if (/CriOS|FxiOS/i.test(ua)) return "safari";
  if (/(iPhone|iPad|iPod)/i.test(ua) && /Safari/i.test(ua) && !/Chrome/i.test(ua)) return "safari";
  return "chrome";
}

/** Whether this browser can record at all — checked before running a countdown. */
export function canRecord() {
  return typeof window.MediaRecorder !== "undefined";
}

/** Whether camera access is even offered by this browser/origin. */
export function canUseCamera() {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

/**
 * Has camera + microphone access already been granted?
 *
 * Used to skip the priming sheet on the second media screen. Once access is granted the
 * browser does not prompt again, so a sheet explaining a prompt that never appears is
 * just something in the way.
 *
 * Answers only "yes, certainly". `permissions.query({name:"camera"})` is unsupported in
 * Safari (it throws rather than reporting "prompt"), so anything short of a definite
 * "granted" comes back false and the caller primes as before.
 *
 * @returns {Promise<boolean>}
 */
export function mediaAccessGranted() {
  if (!navigator.permissions || !navigator.permissions.query) return Promise.resolve(false);
  return Promise.all([
    navigator.permissions.query({ name: "camera" }),
    navigator.permissions.query({ name: "microphone" }),
  ])
    .then((results) => results.every((result) => result.state === "granted"))
    .catch(() => false);
}
